/**
 * RatingBadges Component
 * Displays movie ratings (IMDb, Rotten Tomatoes, Metacritic) with official icons
 */

interface RatingBadgesProps {
  imdbRating?: string | null;
  imdbVotes?: string | null;
  rottenTomatoesRating?: string | null;
  metacriticRating?: string | null;
  showVotes?: boolean;
  compact?: boolean;
}

// Official IMDb icon
function IMDbIcon() {
  return (
    <svg className="rating-icon imdb-icon" viewBox="0 0 122.88 122.88">
      <path
        fill="#F5C518"
        d="M18.43,0h86.02c10.18,0,18.43,8.25,18.43,18.43v86.02c0,10.18-8.25,18.43-18.43,18.43H18.43 C8.25,122.88,0,114.63,0,104.45l0-86.02C0,8.25,8.25,0,18.43,0L18.43,0z"
      />
      <path
        fill="#000"
        d="M24.96,78.72V44.16h-9.6v34.56H24.96L24.96,78.72z M45.36,44.16L43.2,60.24L42,51.6l-1.2-7.44l-12,0v34.56h8.16v-22.8 l3.36,22.8h6l3.12-23.28v23.28h8.16V44.16H45.36L45.36,44.16z M61.44,78.72V44.16h14.88c3.6,0,6.24,2.64,6.24,6v22.56 c0,3.36-2.64,6-6.24,6H61.44L61.44,78.72z M72.72,50.4l-2.16-0.24v22.56c1.2,0,2.16-0.24,2.4-0.72c0.48-0.48,0.48-1.92,0.48-4.32 V54.24v-2.88L72.72,50.4L72.72,50.4L72.72,50.4z M100.56,52.8h0.72c3.36,0,6.24,2.64,6.24,6v13.92c0,3.36-2.88,6-6.24,6l-0.72,0 c-1.92,0-3.84-0.96-5.04-2.64l-0.48,2.16H86.4V44.16h9.12V55.2C96.72,53.76,98.64,52.8,100.56,52.8L100.56,52.8z M98.64,69.6v-8.16 L98.4,58.8c-0.24-0.48-0.96-0.72-1.44-0.72c-0.48,0-1.2,0.24-1.44,0.72v13.68c0.24,0.48,0.96,0.72,1.44,0.72 c0.48,0,1.44-0.24,1.44-0.72L98.64,69.6L98.64,69.6z"
      />
    </svg>
  );
}

// Official Rotten Tomatoes tomato icon
function RTIcon() {
  return (
    <svg className="rating-icon rt-icon" viewBox="0 0 138.75 141.25">
      <g fill="#f93208">
        <path d="m20.154 40.829c-28.149 27.622-13.657 61.011-5.734 71.931 35.254 41.954 92.792 25.339 111.89-5.9071 4.7608-8.2027 22.554-53.467-23.976-78.009z" />
        <path d="m39.613 39.265 4.7778-8.8607 28.406-5.0384 11.119 9.2082z" />
      </g>
      <path
        fill="#02902e"
        d="m39.436 8.5696 8.9682-5.2826 6.7569 15.479c3.7925-6.3226 13.79-16.316 24.939-4.6684-4.7281 1.2636-7.5161 3.8553-7.7397 8.4768 15.145-4.1697 31.343 3.2127 33.539 9.0911-10.951-4.314-27.695 10.377-41.771 2.334 0.009 15.045-12.617 16.636-19.902 17.076 2.077-4.996 5.591-9.994 1.474-14.987-7.618 8.171-13.874 10.668-33.17 4.668 4.876-1.679 14.843-11.39 24.448-11.425-6.775-2.467-12.29-2.087-17.814-1.475 2.917-3.961 12.149-15.197 28.625-8.476z"
      />
    </svg>
  );
}

// Official Metacritic icon
function MetacriticIcon() {
  return (
    <svg className="rating-icon metacritic-icon" viewBox="0 0 88 88">
      <circle fill="#FFCC00" cx="44" cy="44" r="44" />
      <circle fill="#001B36" stroke="#FFCC00" strokeWidth="4.6" cx="44" cy="44" r="41.6" />
      <path
        fill="#FFF"
        transform="translate(-10-961) matrix(1.2756629,-1.3487733,1.3685717,1.2634987,-267.04706,1066.0743)"
        d="m126.73438,92.087002 5.05859,0 0,2.832031 c 1.80989-2.200501 3.96483-3.30076 6.46484-3.300781 1.32811,2.1e-5 2.48045,.273458 3.45703,.820312 .97655,.546895 1.77733,1.373717 2.40235,2.480469 .91144-1.106752 1.89451-1.933574 2.94922-2.480469 1.05466-0.546854 2.18096-0.820291 3.3789-0.820312 1.52341,2.1e-5 2.81247,.309265 3.86719,.927734 1.05466,.618509 1.84242,1.526711 2.36328,2.724609 .37757,.885434 .56637,2.317724 .56641,4.296875 l 0,13.26172-5.48828,0 0-11.85547 c-3e-5-2.057277-0.18883-3.385401-0.56641-3.984375-0.50784-0.781233-1.28909-1.171858-2.34375-1.171875-0.76825,1.7e-5-1.49091,.234392-2.16797,.703125-0.6771,.468766-1.16538,1.155614-1.46484,2.060547-0.2995,.904961-0.44924,2.333998-0.44922,4.287108 l 0,9.96094-5.48828,0 0-11.36719 c-2e-5-2.018214-0.0977-3.320296-0.29297-3.906248-0.19533-0.585922-0.49806-1.02212-0.9082-1.308594-0.41017-0.286442-0.96681-0.429671-1.66993-0.429688-0.84636,1.7e-5-1.60808,.227882-2.28515,.683594-0.6771,.455745-1.16212,1.113297-1.45508,1.972656-0.29298,.859389-0.43946,2.28517-0.43945,4.27734 l 0,10.07813-5.48828,0z"
      />
    </svg>
  );
}

export default function RatingBadges({
  imdbRating,
  imdbVotes,
  rottenTomatoesRating,
  metacriticRating,
  showVotes = false,
  compact = false,
}: RatingBadgesProps) {
  const hasRatings = imdbRating || rottenTomatoesRating || metacriticRating;

  if (!hasRatings) return null;

  return (
    <div className={`rating-badges ${compact ? 'rating-badges-compact' : ''}`}>
      {imdbRating && imdbRating !== 'N/A' && (
        <div className="rating-badge imdb-badge">
          <IMDbIcon />
          <span className="rating-value">{imdbRating}</span>
          {showVotes && imdbVotes && <span className="rating-votes">({imdbVotes})</span>}
        </div>
      )}
      {rottenTomatoesRating && rottenTomatoesRating !== 'N/A' && (
        <div className="rating-badge rt-badge">
          <RTIcon />
          <span className="rating-value">{rottenTomatoesRating}</span>
        </div>
      )}
      {metacriticRating && metacriticRating !== 'N/A' && (
        <div className="rating-badge metacritic-badge">
          <MetacriticIcon />
          <span className="rating-value">{metacriticRating}</span>
        </div>
      )}
    </div>
  );
}
